import React, { useState, useEffect, useContext, useRef } from "react";
import { useTheme } from "@mui/material/styles";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import whatsappIcon from "../../assets/nopicture.png";
import { i18n } from "../../translate/i18n";
import {
  PersonOutlined, CloseOutlined, CheckOutlined,
  BadgeOutlined, LinkOutlined, AdminPanelSettingsOutlined, TuneOutlined,
  ConfirmationNumberOutlined, ExploreOutlined, PaidOutlined,
} from "@mui/icons-material";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";

import { Can } from "../Can";
import { Avatar, Grid, Input, Paper, Box, Typography, IconButton, useMediaQuery } from "@mui/material";
import { getBackendUrl } from "../../config";
import TabPanel from "../TabPanel";
import AvatarUploader from "../AvatarUpload";
import useGoogleIdentity from "../../hooks/useGoogleIdentity";
import ResponsiveSheet from "../ResponsiveSheet";
import { SectionCard, FieldRow, OptionRow, ToggleField } from "../Settings/OptionsUI";

const backendUrl = getBackendUrl();

// Mismo ícono de Google que usa Login/index.js -- ver TAREA: vinculación
// de Google, "mismo ícono y tratamiento del login".
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18">
    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
    <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
  </svg>
);

// Ver TAREA: vinculación de Google desde el perfil -- reemplaza el
// campo de texto emailAlterno de la Fase 2. Sin vincular: botón que
// dispara el mismo flujo de Google Identity Services que el login
// (useGoogleIdentity). Vinculado: bloque de acento con el correo y un
// enlace para desvincular, que pide la contraseña actual antes de
// limpiar emailAlterno/googleId (mismo criterio que esAutoEdicion usa
// para el cambio de contraseña).
//
// Ver TAREA: reorganización SectionCard (mockup
// https://claude.ai/artifact/M3q9ar6VJft7zQ39HCyZBL) -- este bloque
// mantiene EXACTAMENTE el mismo contenido/lógica de siempre, solo el
// acento verde se armoniza con los tokens de borde/fondo que ya usa
// SectionCard (0.5px solid, radios 10-12px) en vez de los valores
// sueltos que tenía antes.
function GoogleLinkSection({ emailAlterno, onLinked, onUnlinked }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [linking, setLinking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [unlinkError, setUnlinkError] = useState("");
  const [unlinking, setUnlinking] = useState(false);

  const handleCredential = async (idToken) => {
    setLinking(true);
    try {
      const { data } = await api.post("/auth/vincular-google", { idToken });
      toast.success(i18n.t("userModal.google.linkedSuccess"));
      onLinked(data.emailAlterno);
    } catch (err) {
      toastError(err);
    } finally {
      setLinking(false);
    }
  };

  const triggerGoogleLink = useGoogleIdentity(handleCredential);

  const openConfirm = () => {
    setCurrentPassword("");
    setUnlinkError("");
    setConfirmOpen(true);
  };

  const handleUnlink = async () => {
    setUnlinkError("");
    if (!currentPassword) {
      setUnlinkError(i18n.t("userModal.google.currentPasswordRequired"));
      return;
    }
    setUnlinking(true);
    try {
      await api.post("/auth/desvincular-google", { currentPassword });
      toast.success(i18n.t("userModal.google.unlinkedSuccess"));
      setConfirmOpen(false);
      onUnlinked();
    } catch (err) {
      setUnlinkError(err.response?.data?.error || i18n.t("userModal.google.unlinkError"));
    } finally {
      setUnlinking(false);
    }
  };

  if (emailAlterno) {
    return (
      <Box sx={{
        border: `0.5px solid ${isDark ? "rgba(52,168,83,0.35)" : "rgba(52,168,83,0.3)"}`,
        background: isDark ? "rgba(52,168,83,0.08)" : "rgba(52,168,83,0.06)",
        borderRadius: "12px", padding: "13px 14px",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <CheckOutlined sx={{ fontSize: 16, color: "#34A853" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: isDark ? "rgba(255,255,255,0.88)" : "rgba(0,0,0,0.82)" }}>
            {i18n.t("userModal.google.linkedTitle")}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.6)", marginTop: "4px", marginLeft: "24px" }}>
          {emailAlterno}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)", marginTop: "2px", marginLeft: "24px" }}>
          {i18n.t("userModal.google.linkedHint")}
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={openConfirm}
          sx={{
            border: "none", background: "none", cursor: "pointer", padding: 0, marginTop: "8px", marginLeft: "24px",
            fontSize: 12, fontWeight: 600, color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
            textDecoration: "underline", "&:hover": { color: "#ef4444" },
          }}
        >
          {i18n.t("userModal.google.unlink")}
        </Box>

        {/* RDS-ZINDEX-AUDIT: este Dialog se abre desde GoogleLinkSection,
            que es contenido compartido del ResponsiveSheet (1301) de
            UserModal en movil -- sin zIndex propio (default 1300 de MUI)
            quedaba invisible detras del sheet, igual que ColorBoxModal en
            QueueModal y ConfirmationModal en QuickMessageDialog. Local a
            este archivo, no un componente reusado -- se fija el valor
            directo, sin agregar un prop. */}
        <Dialog open={confirmOpen} onClose={unlinking ? undefined : () => setConfirmOpen(false)} maxWidth="xs" fullWidth
          sx={{ zIndex: 1310 }}
          PaperProps={{ sx: { borderRadius: "14px" } }}>
          <Box sx={{ padding: "20px 22px" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, marginBottom: "6px" }}>
              {i18n.t("userModal.google.unlinkConfirmTitle")}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: theme.palette.text.secondary, marginBottom: "14px" }}>
              {i18n.t("userModal.google.unlinkConfirmBody")}
            </Typography>
            <TextField
              type="password"
              fullWidth
              size="small"
              autoFocus
              label={i18n.t("userModal.google.currentPassword")}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
            />
            {unlinkError && (
              <Typography sx={{ fontSize: 12, color: "#ef4444", marginTop: "8px" }}>{unlinkError}</Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, marginTop: "18px" }}>
              <Button onClick={() => setConfirmOpen(false)} disabled={unlinking} sx={{ textTransform: "none" }}>
                {i18n.t("userModal.buttons.cancel")}
              </Button>
              <Button onClick={handleUnlink} disabled={unlinking} variant="contained" color="error" sx={{ textTransform: "none" }}
                startIcon={unlinking ? <CircularProgress size={14} color="inherit" /> : null}>
                {i18n.t("userModal.google.unlink")}
              </Button>
            </Box>
          </Box>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{
      border: `0.5px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
      borderRadius: "12px", padding: "13px 14px",
    }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: isDark ? "rgba(255,255,255,0.82)" : "rgba(0,0,0,0.78)", marginBottom: "4px" }}>
        {i18n.t("userModal.google.title")}
      </Typography>
      <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, marginBottom: "10px" }}>
        {i18n.t("userModal.google.description")}
      </Typography>
      <Box
        component="button"
        type="button"
        onClick={triggerGoogleLink}
        disabled={linking}
        sx={{
          height: 40, borderRadius: "9px",
          border: isDark ? "0.5px solid rgba(255,255,255,0.15)" : "0.5px solid rgba(0,0,0,0.15)",
          background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
          display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
          fontSize: 13, fontWeight: 600, cursor: linking ? "default" : "pointer", width: "100%",
          color: isDark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.70)",
          opacity: linking ? 0.6 : 1,
          "&:hover": !linking && {
            background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.02)",
          },
        }}
      >
        {linking ? <CircularProgress size={16} /> : <GoogleIcon />}
        {i18n.t("userModal.google.linkButton")}
      </Box>
    </Box>
  );
}

// Pill de pestaña -- mismo patrón exacto de pages/Settings/index.js
// (Box con bgcolor/borderColor condicional al estado activo), no un
// componente nuevo inventado -- se copia el mismo sx para que
// "Editar usuario" luzca igual a Opciones/Empresas/Planes.
function TabPill({ label, active, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        fontSize: 12.5, fontWeight: 600, px: 1.5, py: 0.75, borderRadius: "20px", cursor: "pointer",
        border: "0.5px solid", borderColor: active ? "primary.main" : "divider",
        bgcolor: active ? "primary.main" : "action.hover",
        color: active ? "#fff" : "text.secondary",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

const formatDateForInput = (date) => {
  if (!date) return '';

  // Se já está no formato YYYY-MM-DD, retorna como está
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  // Se é uma data ISO ou objeto Date, converte para YYYY-MM-DD
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  // Usar métodos locais para evitar problemas de timezone
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const parseDateFromInput = (dateString) => {
  if (!dateString) return null;

  // Se já está no formato YYYY-MM-DD, retorna como está
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Se é uma data ISO, extrai apenas a parte da data
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }

  return dateString;
};


const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
  email: Yup.string().email("Invalid email").required("Required"),
  allHistoric: Yup.string().nullable(),
});


const UserModal = ({ open, onClose, userId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const initialState = {
    name: "",
    email: "",
    password: "",
    birthDate: "",
    profile: "user",
    startWork: "00:00",
    endWork: "23:59",
    farewellMessage: "",
    allTicket: "enable",
    allowGroup: false,
    defaultTheme: "light",
    defaultMenu: "open",
    allHistoric: "enabled",
    allUserChat: "enabled",
    userClosePendingTicket: "enabled",
    showDashboard: "enabled",
    allowRealTime: "enabled",
    allowConnections: "enabled",
    showContacts: "enabled",
    showCampaign: "enabled",
    showFlow: "enabled",
    finalizacaoComValorVendaAtiva: "false",
    allowSeeMessagesInPendingTickets: "enabled"
  };

  const { user: loggedInUser } = useContext(AuthContext);

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [whatsappId, setWhatsappId] = useState(false);
  // const [allTicket, setAllTicket] = useState("disable");
  const { loading, whatsApps } = useWhatsApps();
  const [profileUrl, setProfileUrl] = useState(null);
  const [tab, setTab] = useState("general");
  const [avatar, setAvatar] = useState(null);
  const startWorkRef = useRef();
  const endWorkRef = useRef();

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser((prevState) => ({
          ...prevState,
          ...data,
          allTicket:
            data.allTicket === "enable" || data.allTicket === "enabled"
              ? "enable"
              : "disable",
          finalizacaoComValorVendaAtiva: data.finalizacaoComValorVendaAtiva
            ? "true"
            : "false",
          allowSeeMessagesInPendingTickets: data.allowSeeMessagesInPendingTickets === "enabled" ? "enabled" : "disabled",
          farewellMessage: data.farewellMessage || "",
          // Formatar a data corretamente
          birthDate: formatDateForInput(data.birthDate)
        }));

        const { profileImage } = data;
        setProfileUrl(
          `${backendUrl}/public/company${data.companyId}/user/${profileImage}`
        );

        const userQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(userQueueIds);
        setWhatsappId(data.whatsappId ? data.whatsappId : "");
      } catch (err) {
        toastError(err);
      }
    };

    fetchUser();
  }, [userId, open]);

  const handleClose = () => {
    onClose();
    setUser(initialState);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const getBasename = (filepath) => {
    if (!filepath) return '';
    // Remove query strings e hashes
    const cleanPath = filepath.split('?')[0].split('#')[0];
    // Pega o último segmento após /
    const segments = cleanPath.split('/');
    return segments[segments.length - 1];
  };

  // UserModal/index.js - Função handleSaveUser corrigida
  const handleSaveUser = async (values) => {
    const uploadAvatar = async (userId) => {
      if (!avatar || typeof avatar !== 'object') return null;

      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("typeArch", "user");
      formData.append("profileImage", avatar);

      try {
        const { data } = await api.post(
          `/users/${userId}/media-upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        return data.user.profileImage;
      } catch (error) {
        throw error;
      }
    };

    const userData = {
      ...values,
      whatsappId,
      queueIds: selectedQueueIds,
      finalizacaoComValorVendaAtiva: values.finalizacaoComValorVendaAtiva === "true",
      birthDate: parseDateFromInput(values.birthDate),
      allowSeeMessagesInPendingTickets: values.allowSeeMessagesInPendingTickets === "enabled" ? "enabled" : "disabled"
    };

    try {
      let responseData;

      if (userId) {
        // Atualizar usuário existente
        const { data } = await api.put(`/users/${userId}`, userData);
        responseData = data;

        // Upload da imagem se houver uma nova
        if (avatar && typeof avatar === 'object') {
          const newProfileImage = await uploadAvatar(userId);
          if (newProfileImage) {
            responseData.profileImage = newProfileImage;

            // Atualizar localStorage se for o usuário logado
            if (userId === loggedInUser.id) {
              localStorage.setItem("profileImage", newProfileImage);
            }
          }
        }
      } else {
        // Criar novo usuário
        const { data } = await api.post("/users", userData);
        responseData = data.user;

        // Upload da imagem se houver
        if (avatar && typeof avatar === 'object' && responseData.id) {
          const newProfileImage = await uploadAvatar(responseData.id);
          if (newProfileImage) {
            responseData.profileImage = newProfileImage;
          }
        }
      }

      handleClose();
      toast.success(i18n.t("userModal.success"));

      // Recarregar página se for o usuário logado para atualizar a interface
      if (userId === loggedInUser.id) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (err) {
      toastError(err);
    }
  };

  // Contenido de ambos tabs -- extraído a una función (no un componente
  // aparte) porque necesita las mismas variables de Formik (touched,
  // errors, setFieldValue) que ya vienen del render-prop de <Formik>,
  // sin tener que pasarlas por props a un componente hijo. Se llama
  // UNA sola vez por render, desde dentro de las dos ramas del
  // contenedor (Dialog en desktop / ResponsiveSheet en móvil) -- mismo
  // JSX, cero duplicación.
  const renderTabsAndContent = ({ touched, errors, setFieldValue }) => (
    <>
      <Box sx={{ display: "flex", gap: 1, px: "18px", pb: "12px", flexShrink: 0 }}>
        <TabPill label={i18n.t("userModal.tabs.general")} active={tab === "general"} onClick={() => setTab("general")} />
        <TabPill label={i18n.t("userModal.tabs.permissions")} active={tab === "permissions"} onClick={() => setTab("permissions")} />
      </Box>

      <Box sx={{
        flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 18px 18px",
        '&::-webkit-scrollbar': { width: '3px' },
        '&::-webkit-scrollbar-thumb': { background: (theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"), borderRadius: '3px' },
      }}>
        <TabPanel value={tab} name={"general"}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
            <AvatarUploader
              setAvatar={setAvatar}
              avatar={user.profileImage}
              companyId={user.companyId}
            />
            {user.profileImage && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                sx={{ mt: 1, fontSize: 11.5, fontWeight: 700, borderRadius: "8px", textTransform: "none" }}
                onClick={() => {
                  user.profileImage = null;
                  setFieldValue("profileImage", null);
                  setAvatar(null);
                }}
              >
                {i18n.t("userModal.title.removeImage")}
              </Button>
            )}
          </Box>

          <SectionCard icon={<BadgeOutlined />} title="Datos de la cuenta">
            <FieldRow>
              <Field
                as={TextField}
                label={i18n.t("userModal.form.name")}
                autoFocus
                name="name"
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </FieldRow>
            <FieldRow>
              <Grid container spacing={1}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Field
                    as={TextField}
                    label={i18n.t("userModal.form.password")}
                    type="password"
                    name="password"
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Field
                    as={TextField}
                    label={i18n.t("userModal.form.email")}
                    name="email"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </FieldRow>
          </SectionCard>

          {/* Ver TAREA: vinculación de Google -- solo aparece cuando el
              usuario edita SU PROPIA cuenta, mismo chequeo de siempre. */}
          {userId === loggedInUser.id && (
            <SectionCard icon={<LinkOutlined />} title="Cuenta vinculada">
              <Box sx={{ "&&": { padding: 0, border: "none" } }}>
                <GoogleLinkSection
                  emailAlterno={user.emailAlterno}
                  onLinked={(email) => setUser((prev) => ({ ...prev, emailAlterno: email }))}
                  onUnlinked={() => setUser((prev) => ({ ...prev, emailAlterno: null }))}
                />
              </Box>
            </SectionCard>
          )}

          {/* Mismo permiso que ya gateaba el Select de Perfil antes de esta
              ronda (user-modal:editProfile) -- ahora también decide si la
              tarjeta ENTERA se muestra, para no dejarle a un perfil "user"
              un encabezado de sección vacío. Los 2 campos de adentro siguen
              con su propio <Can> individual, sin cambios (Departamentos
              sigue gateado por "editQueues", un permiso distinto). */}
          <Can
            role={loggedInUser.profile}
            perform="user-modal:editProfile"
            yes={() => (
              <SectionCard icon={<AdminPanelSettingsOutlined />} title="Rol y accesos">
                <FieldRow>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel id="profile-selection-input-label">
                      {i18n.t("userModal.form.profile")}
                    </InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("userModal.form.profile")}
                      name="profile"
                      labelId="profile-selection-label"
                      id="profile-selection"
                      required
                    >
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="user">User</MenuItem>
                    </Field>
                  </FormControl>
                </FieldRow>

                <FieldRow>
                  <Can
                    role={loggedInUser.profile}
                    perform="user-modal:editQueues"
                    yes={() => (
                      <QueueSelect
                        selectedQueueIds={selectedQueueIds}
                        onChange={(values) => setSelectedQueueIds(values)}
                        fullWidth
                      />
                    )}
                  />
                </FieldRow>

                <FieldRow>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>{i18n.t("userModal.form.whatsapp")}</InputLabel>
                    <Field
                      as={Select}
                      value={whatsappId}
                      onChange={(e) => setWhatsappId(e.target.value)}
                      label={i18n.t("userModal.form.whatsapp")}
                    >
                      <MenuItem value={""}>&nbsp;</MenuItem>
                      {whatsApps.map((whatsapp) => (
                        <MenuItem key={whatsapp.id} value={whatsapp.id}>
                          {whatsapp.name}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </FieldRow>

                <FieldRow>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Field
                        as={TextField}
                        label={i18n.t("userModal.form.startWork")}
                        type="time"
                        ampm={"false"}
                        inputRef={startWorkRef}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 600 }}
                        fullWidth
                        name="startWork"
                        error={touched.startWork && Boolean(errors.startWork)}
                        helperText={touched.startWork && errors.startWork}
                        variant="outlined"
                        margin="dense"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Field
                        as={TextField}
                        label={i18n.t("userModal.form.endWork")}
                        type="time"
                        ampm={"false"}
                        inputRef={endWorkRef}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 600 }}
                        fullWidth
                        name="endWork"
                        error={touched.endWork && Boolean(errors.endWork)}
                        helperText={touched.endWork && errors.endWork}
                        variant="outlined"
                        margin="dense"
                      />
                    </Grid>
                  </Grid>
                </FieldRow>
              </SectionCard>
            )}
          />

          <SectionCard icon={<TuneOutlined />} title="Preferencias">
            <FieldRow>
              <Field
                as={TextField}
                label="Fecha de nacimiento"
                type="date"
                name="birthDate"
                InputLabelProps={{ shrink: true }}
                fullWidth
                variant="outlined"
                margin="dense"
                helperText="Para notificaciones de cumpleaños"
                onChange={(e) => {
                  const formattedDate = parseDateFromInput(e.target.value);
                  setFieldValue('birthDate', formattedDate);
                }}
              />
            </FieldRow>
            <FieldRow>
              <Field
                as={TextField}
                label={i18n.t("userModal.form.farewellMessage")}
                type="farewellMessage"
                multiline
                rows={4}
                fullWidth
                name="farewellMessage"
                error={touched.farewellMessage && Boolean(errors.farewellMessage)}
                helperText={touched.farewellMessage && errors.farewellMessage}
                variant="outlined"
                margin="dense"
              />
            </FieldRow>
            <FieldRow>
              <Grid container spacing={1}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>{i18n.t("userModal.form.defaultTheme")}</InputLabel>
                    <Field as={Select} label={i18n.t("userModal.form.defaultTheme")} name="defaultTheme" type="defaultTheme" required>
                      <MenuItem value="light">{i18n.t("userModal.form.defaultThemeLight")}</MenuItem>
                      <MenuItem value="dark">{i18n.t("userModal.form.defaultThemeDark")}</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>{i18n.t("userModal.form.defaultMenu")}</InputLabel>
                    <Field as={Select} label={i18n.t("userModal.form.defaultMenu")} name="defaultMenu" type="defaultMenu" required>
                      <MenuItem value={"open"}>{i18n.t("userModal.form.defaultMenuOpen")}</MenuItem>
                      <MenuItem value={"closed"}>{i18n.t("userModal.form.defaultMenuClosed")}</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>
              </Grid>
            </FieldRow>
          </SectionCard>
        </TabPanel>

        <TabPanel value={tab} name={"permissions"}>
          {/* Mismo <Can> único que ya envolvía las 11+1 opciones antes de
              esta ronda -- ahora envuelve las 3 SectionCard en vez de los
              <Grid> sueltos, sin cambiar el permiso ni el alcance. */}
          <Can
            role={loggedInUser.profile}
            perform="user-modal:editProfile"
            yes={() => (
              <>
                <SectionCard icon={<ConfirmationNumberOutlined />} title="Tickets">
                  <Field name="allTicket">
                    {({ field, form }) => (
                      <OptionRow
                        label={i18n.t("userModal.form.allTicket")}
                        control={<ToggleField checked={field.value === "enable"} onChange={(v) => form.setFieldValue("allTicket", v ? "enable" : "disable")} />}
                      />
                    )}
                  </Field>
                  <Field name="allowGroup">
                    {({ field, form }) => (
                      <OptionRow
                        label={i18n.t("userModal.form.allowGroup")}
                        control={<ToggleField checked={!!field.value} onChange={(v) => form.setFieldValue("allowGroup", v)} />}
                      />
                    )}
                  </Field>
                  <Field name="allHistoric">
                    {({ field, form }) => (
                      <OptionRow
                        label={i18n.t("userModal.form.allHistoric")}
                        control={<ToggleField checked={field.value === "enabled"} onChange={(v) => form.setFieldValue("allHistoric", v ? "enabled" : "disabled")} />}
                      />
                    )}
                  </Field>
                  <Field name="userClosePendingTicket">
                    {({ field, form }) => (
                      <OptionRow
                        label={i18n.t("userModal.form.userClosePendingTicket")}
                        control={<ToggleField checked={field.value === "enabled"} onChange={(v) => form.setFieldValue("userClosePendingTicket", v ? "enabled" : "disabled")} />}
                      />
                    )}
                  </Field>
                  <Field name="allowSeeMessagesInPendingTickets">
                    {({ field, form }) => (
                      <OptionRow
                        label={i18n.t("userModal.form.allowSeeMessagesInPendingTickets")}
                        control={<ToggleField checked={field.value === "enabled"} onChange={(v) => form.setFieldValue("allowSeeMessagesInPendingTickets", v ? "enabled" : "disabled")} />}
                      />
                    )}
                  </Field>
                </SectionCard>

                <SectionCard icon={<ExploreOutlined />} title="Navegación">
                  <Field name="allUserChat">
                    {({ field, form }) => (
                      <OptionRow
                        label={i18n.t("userModal.form.allUserChat")}
                        control={<ToggleField checked={field.value === "enabled"} onChange={(v) => form.setFieldValue("allUserChat", v ? "enabled" : "disabled")} />}
                      />
                    )}
                  </Field>
                  <Field name="allowConnections">
                    {({ field, form }) => (
                      <OptionRow
                        label={i18n.t("userModal.form.allowConnections")}
                        control={<ToggleField checked={field.value === "enabled"} onChange={(v) => form.setFieldValue("allowConnections", v ? "enabled" : "disabled")} />}
                      />
                    )}
                  </Field>
                  <Field name="showDashboard">
                    {({ field, form }) => (
                      <OptionRow
                        label={i18n.t("userModal.form.showDashboard")}
                        control={<ToggleField checked={field.value === "enabled"} onChange={(v) => form.setFieldValue("showDashboard", v ? "enabled" : "disabled")} />}
                      />
                    )}
                  </Field>
                  <Field name="allowRealTime">
                    {({ field, form }) => (
                      <OptionRow
                        label={i18n.t("userModal.form.allowRealTime")}
                        control={<ToggleField checked={field.value === "enabled"} onChange={(v) => form.setFieldValue("allowRealTime", v ? "enabled" : "disabled")} />}
                      />
                    )}
                  </Field>
                  <Field name="showContacts">
                    {({ field, form }) => (
                      <OptionRow
                        label={i18n.t("userModal.form.showContacts")}
                        control={<ToggleField checked={field.value === "enabled"} onChange={(v) => form.setFieldValue("showContacts", v ? "enabled" : "disabled")} />}
                      />
                    )}
                  </Field>
                  <Field name="showCampaign">
                    {({ field, form }) => (
                      <OptionRow
                        label={i18n.t("userModal.form.showCampaign")}
                        control={<ToggleField checked={field.value === "enabled"} onChange={(v) => form.setFieldValue("showCampaign", v ? "enabled" : "disabled")} />}
                      />
                    )}
                  </Field>
                  <Field name="showFlow">
                    {({ field, form }) => (
                      <OptionRow
                        label={i18n.t("userModal.form.showFlow")}
                        control={<ToggleField checked={field.value === "enabled"} onChange={(v) => form.setFieldValue("showFlow", v ? "enabled" : "disabled")} />}
                      />
                    )}
                  </Field>
                </SectionCard>

                <SectionCard icon={<PaidOutlined />} title="Cierre de atención">
                  <Field name="finalizacaoComValorVendaAtiva">
                    {({ field, form }) => (
                      <OptionRow
                        label="Registrar valor de venta al cerrar"
                        control={<ToggleField checked={field.value === "true"} onChange={(v) => form.setFieldValue("finalizacaoComValorVendaAtiva", v ? "true" : "false")} />}
                      />
                    )}
                  </Field>
                </SectionCard>
              </>
            )}
          />
        </TabPanel>
      </Box>
    </>
  );

  const renderHeader = () => (
    <Box sx={{ padding: '18px 22px 14px', borderBottom: `0.5px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"}`, flexShrink: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 30, height: 30, borderRadius: '8px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PersonOutlined sx={{ fontSize: 16, color: '#0399FF' }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: (theme.palette.mode === "dark" ? "rgba(255,255,255,0.82)" : "rgba(0,0,0,0.82)") }}>
            {userId ? i18n.t("userModal.title.edit") : i18n.t("userModal.title.add")}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small"
          sx={{ borderRadius: '6px', color: (theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"), '&:hover': { background: (theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)") } }}>
          <CloseOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );

  const renderFooter = (isSubmitting) => (
    <Box sx={{ display: "flex", gap: 1, justifyContent: 'flex-end', padding: '12px 22px', borderTop: `0.5px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"}`, background: theme.palette.mode === "dark" ? "#0d1424" : "#fafafa", flexShrink: 0 }}>
      <Button onClick={handleClose}
        sx={{ fontSize: 12, fontWeight: 500, color: (theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"), border: `0.5px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`, background: theme.palette.background.paper, borderRadius: '8px', textTransform: 'none', '&:hover': { background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#f4f4f4" } }}>
        {i18n.t("userModal.buttons.cancel")}
      </Button>
      <Button type="submit" disabled={isSubmitting}
        sx={{ fontSize: 12, fontWeight: 600, color: '#fff', background: '#0399FF', borderRadius: '8px', textTransform: 'none', px: 2, '&:hover': { background: '#0284d4' }, '&.Mui-disabled': { background: '#b0d9ff', color: '#fff' } }}
        startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : <CheckOutlined sx={{ fontSize: 14 }} />}>
        {userId ? i18n.t("userModal.buttons.okEdit") : i18n.t("userModal.buttons.okAdd")}
      </Button>
    </Box>
  );

  return (
    <Formik
      initialValues={user}
      enableReinitialize={true}
      validationSchema={UserSchema}
      onSubmit={(values, actions) => {
        setTimeout(() => {
          handleSaveUser(values);
          actions.setSubmitting(false);
        }, 400);
      }}
    >
      {({ touched, errors, isSubmitting, setFieldValue }) => (
        isMobile ? (
          // Ver TAREA: reorganización SectionCard -- móvil pasa de Dialog
          // centrado a ResponsiveSheet (mismo patrón de Marketing/
          // Notificaciones/Kanban). El Paper del SwipeableDrawer ya es
          // flex-column con maxHeight fijo; este Box interno replica ese
          // mismo eje (header y footer con alto natural, contenido con
          // flex:1+minHeight:0 como única región que scrollea) para que
          // el footer quede pegado abajo sin position:sticky.
          <ResponsiveSheet open={open} onClose={handleClose} maxHeightMobile="92vh" desktopWidth={460}>
            <Form style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              {renderHeader()}
              {renderTabsAndContent({ touched, errors, setFieldValue })}
              {renderFooter(isSubmitting)}
            </Form>
          </ResponsiveSheet>
        ) : (
          // Desktop -- Dialog real de MUI, sin cambios de contenedor
          // (mismo maxWidth="sm", mismo PaperProps de siempre).
          <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            scroll="paper"
            PaperProps={{ sx: { borderRadius: '14px', border: `0.5px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}` } }}
          >
            <Form>
              {renderHeader()}
              <DialogContent sx={{ padding: 0, display: "flex", flexDirection: "column" }}>
                {renderTabsAndContent({ touched, errors, setFieldValue })}
              </DialogContent>
              {renderFooter(isSubmitting)}
            </Form>
          </Dialog>
        )
      )}
    </Formik>
  );
};

export default UserModal;
